import { Link, NavLink, useNavigate } from "react-router-dom";
import { useState, useEffect, useRef } from "react";
import { useAuth } from "../lib/auth";
import { useI18n } from "../lib/i18n";
import { cx } from "../lib/api";
import LanguageSwitcher from "./LanguageSwitcher";
import NotificationsBell from "./NotificationsBell";
import CurrencySwitcher from "./CurrencySwitcher";
import {
  Menu, X, Sparkles, ChevronDown, LogOut, User as UserIcon,
  Compass, MapPin, Wand2, LogIn, LayoutDashboard, Lock, MessageSquare, Wallet
} from "lucide-react";

const LOGO_FULL = "/brand/logofull.svg";

export function Logo({ className = "" }) {
  return (
    <Link to="/" className={cx("flex items-center", className)} data-testid="logo-link">
      <img src={LOGO_FULL} alt="Meet & Mint Concierge" className="h-9 md:h-10 w-auto" />
    </Link>
  );
}

const DESTINATION_LIST = [
  { slug: "ibiza-spain", name: "Ibiza", country: "Spain", status: "active" },
  { slug: "custom-trip", name: "Custom Trip", country: "Worldwide", status: "active" },
  { slug: "mykonos-greece", name: "Mykonos", country: "Greece", status: "comingSoon" },
  { slug: "dubai-uae", name: "Dubai", country: "UAE", status: "comingSoon" },
  { slug: "miami-usa", name: "Miami", country: "USA", status: "comingSoon" },
  { slug: "tulum-mexico", name: "Tulum", country: "Mexico", status: "comingSoon" },
  { slug: "bali-indonesia", name: "Bali", country: "Indonesia", status: "comingSoon" },
  { slug: "marbella-spain", name: "Marbella", country: "Spain", status: "comingSoon" },
  { slug: "monaco-monaco", name: "Monaco", country: "Monaco", status: "comingSoon" },
  { slug: "saint-tropez-france", name: "Saint-Tropez", country: "France", status: "comingSoon" },
  { slug: "cannes-france", name: "Cannes", country: "France", status: "comingSoon" },
  { slug: "courchevel-france", name: "Courchevel", country: "France", status: "comingSoon" },
];

function DestinationsDropdown() {
  const { t } = useI18n();
  const [open, setOpen] = useState(false);
  const closeTimer = useRef(null);
  const onEnter = () => { if (closeTimer.current) clearTimeout(closeTimer.current); setOpen(true); };
  const onLeave = () => { closeTimer.current = setTimeout(() => setOpen(false), 120); };
  return (
    <div className="relative" onMouseEnter={onEnter} onMouseLeave={onLeave}>
      <button
        type="button"
        className={cx("inline-flex items-center gap-1.5 text-[13px] tracking-wide hover:text-teal transition", open && "text-teal")}
        data-testid="nav-destinations"
        onClick={() => setOpen((v) => !v)}
      >
        <MapPin size={14} /> {t("header.nav_destinations", "Destinations")} <ChevronDown size={12} className={cx("transition", open && "rotate-180")} />
      </button>
      {open && (
        <div className="absolute left-1/2 -translate-x-1/2 top-full mt-3 w-[640px] glass-solid rounded-3xl p-5 z-50 grid grid-cols-2 gap-2 shadow-ink"
             data-testid="destinations-dropdown">
          {DESTINATION_LIST.map((d) => {
            const isActive = d.status === "active";
            return (
              <Link
                key={d.slug}
                to={d.slug === "custom-trip" ? "/custom-trip" : `/destinations/${d.slug}`}
                className="flex items-center justify-between rounded-xl px-3 py-2.5 hover:bg-white/5 transition"
                data-testid={`dropdown-${d.slug}`}
                onClick={() => setOpen(false)}
              >
                <div>
                  <div className="text-sm text-bone">{d.name}</div>
                  <div className="text-xs text-muted">{d.country}</div>
                </div>
                {isActive ? (
                  <span className="chip success !py-0.5 !text-[10px]">{t("common.bookable", "Bookable")}</span>
                ) : (
                  <span className="chip !py-0.5 !text-[10px]"><Lock size={10}/> {t("common.coming_soon", "Soon")}</span>
                )}
              </Link>
            );
          })}
          <div className="col-span-2 mt-2 pt-3 border-t hairline flex items-center justify-between">
            <span className="text-xs text-muted">{t("header.destinations_curated", "12 destinations curated")}</span>
            <Link to="/destinations" onClick={() => setOpen(false)} className="text-xs text-teal hover:underline">{t("header.view_all", "View all")} →</Link>
          </div>
        </div>
      )}
    </div>
  );
}

export default function Header({ onOpenConcierge }) {
  const { user, logout } = useAuth();
  const { t } = useI18n();
  const [mobileOpen, setMobileOpen] = useState(false);
  const [userMenuOpen, setUserMenuOpen] = useState(false);
  const navigate = useNavigate();
  const userMenuRef = useRef(null);

  useEffect(() => {
    const onClick = (e) => { if (userMenuRef.current && !userMenuRef.current.contains(e.target)) setUserMenuOpen(false); };
    document.addEventListener("mousedown", onClick);
    return () => document.removeEventListener("mousedown", onClick);
  }, []);

  return (
    <header className="fixed top-0 inset-x-0 z-40">
      <div className="glass border-b hairline">
        <div className="mx-auto max-w-7xl px-5 md:px-8 h-[68px] flex items-center justify-between gap-4">
          <Logo />

          <nav className="hidden lg:flex items-center gap-7 text-[13px] tracking-wide">
            <NavLink to="/destinations/ibiza-spain"
                     className={({ isActive }) => cx("inline-flex items-center gap-1.5 hover:text-teal transition", isActive && "text-teal")}
                     data-testid="nav-ibiza">
              <Compass size={14} /> {t("header.nav_ibiza", "Ibiza")}
            </NavLink>
            <DestinationsDropdown />
            <NavLink to="/custom-trip"
                     className={({ isActive }) => cx("inline-flex items-center gap-1.5 hover:text-teal transition", isActive && "text-teal")}
                     data-testid="nav-custom">
              <Wand2 size={14} /> {t("header.nav_custom_trip", "Custom Trip")}
            </NavLink>
            <button onClick={onOpenConcierge}
                    className="inline-flex items-center gap-1.5 text-[13px] tracking-wide hover:text-teal transition"
                    data-testid="btn-open-concierge-header">
              <Sparkles size={14} /> {t("header.nav_concierge", "Concierge")}
            </button>
          </nav>

          <div className="flex items-center gap-2 lg:gap-3">
            <span className="hidden lg:inline-block">
              <LanguageSwitcher />
            </span>

            <span className="hidden md:inline-block">
              <CurrencySwitcher />
            </span>

            {user && <NotificationsBell />}

            {user === undefined ? null : !user ? (
              <Link to="/login"
                    className="btn btn-primary !py-2 !px-4 text-[13px] whitespace-nowrap"
                    data-testid="btn-login-header">
                <LogIn size={14} /> {t("common.sign_in", "Sign in")}
              </Link>
            ) : (
              <div className="relative" ref={userMenuRef}>
                <button onClick={() => setUserMenuOpen((v) => !v)}
                        className="btn btn-outline !py-2 !px-3 text-[13px] whitespace-nowrap"
                        data-testid="btn-user-menu">
                  {user.avatar_url ? (
                    <img src={user.avatar_url.startsWith("http") ? user.avatar_url : `${process.env.REACT_APP_BACKEND_URL}${user.avatar_url}`}
                         alt="" className="h-5 w-5 rounded-full object-cover -ml-1"/>
                  ) : (
                    <UserIcon size={14} />
                  )}
                  {user.name?.split(" ")[0] || t("common.dashboard", "Account")}
                  <ChevronDown size={14} />
                </button>
                {userMenuOpen && (
                  <div className="absolute right-0 mt-2 w-56 glass-solid rounded-2xl p-2 shadow-ink" data-testid="user-menu">
                    <button onClick={() => { setUserMenuOpen(false); navigate("/dashboard"); }}
                            className="w-full flex items-center gap-2 text-left px-3 py-2 rounded-xl hover:bg-white/5"
                            data-testid="menu-dashboard">
                      <LayoutDashboard size={14}/> {t("common.dashboard", "Dashboard")}
                    </button>
                    <button onClick={() => { setUserMenuOpen(false); navigate("/profile"); }}
                            className="w-full flex items-center gap-2 text-left px-3 py-2 rounded-xl hover:bg-white/5"
                            data-testid="menu-profile">
                      <UserIcon size={14}/> {t("header.menu_profile", "Profile")}
                    </button>
                    <button onClick={() => { setUserMenuOpen(false); navigate("/wallet"); }}
                            className="w-full flex items-center gap-2 text-left px-3 py-2 rounded-xl hover:bg-white/5"
                            data-testid="menu-wallet">
                      <Wallet size={14}/> {t("header.menu_wallet", "Wallet")}
                    </button>
                    <div className="px-3 py-1 text-[10px] tracking-widest2 uppercase text-muted">{t("header.signed_in_as", "Signed in as")}</div>
                    <div className="px-3 pb-1 text-xs text-muted truncate">{user.email}</div>
                    {user.username && <div className="px-3 pb-2 text-[11px] font-mono text-teal/80">@{user.username}</div>}
                    <div className="divider-x my-1"/>
                    <button onClick={async () => { await logout(); setUserMenuOpen(false); navigate("/"); }}
                            className="w-full flex items-center gap-2 text-left px-3 py-2 rounded-xl hover:bg-white/5 text-rose-300"
                            data-testid="menu-logout">
                      <LogOut size={14}/> {t("common.sign_out", "Sign out")}
                    </button>
                  </div>
                )}
              </div>
            )}

            <button onClick={() => setMobileOpen((v) => !v)}
                    className="lg:hidden p-2 -mr-2"
                    data-testid="btn-mobile-menu"
                    aria-label="Menu">
              {mobileOpen ? <X size={20}/> : <Menu size={20}/>}
            </button>
          </div>
        </div>

        {mobileOpen && (
          <div className="lg:hidden border-t hairline">
            <div className="px-5 py-4 grid gap-3 text-sm">
              <Link to="/destinations/ibiza-spain" onClick={() => setMobileOpen(false)} className="flex items-center gap-2" data-testid="mnav-ibiza"><Compass size={14}/> {t("header.nav_ibiza", "Ibiza")}</Link>
              <Link to="/destinations" onClick={() => setMobileOpen(false)} className="flex items-center gap-2" data-testid="mnav-dest"><MapPin size={14}/> {t("header.all_destinations", "All destinations")}</Link>
              <Link to="/custom-trip" onClick={() => setMobileOpen(false)} className="flex items-center gap-2" data-testid="mnav-custom"><Wand2 size={14}/> {t("header.nav_custom_trip", "Custom Trip")}</Link>
              <button onClick={() => { setMobileOpen(false); onOpenConcierge?.(); }} className="flex items-center gap-2 text-left text-teal" data-testid="mnav-concierge"><MessageSquare size={14}/> {t("header.nav_concierge", "Concierge")}</button>
              <div className="pt-2 border-t hairline mt-2">
                <LanguageSwitcher />
              </div>
            </div>
          </div>
        )}
      </div>
    </header>
  );
}
